/**
 * Pillar workflow stepper.
 *
 * Replaces the React state that drove [data-step] in the original. All the
 * visuals — active opacity, accent colour, the progress bar, the cross-fading
 * image — are CSS keyed off that attribute, so this only advances one integer.
 *
 * Behaviour matches the original: autoplay on a fixed interval, click a step to
 * jump to it, pause while the pointer is over the track, and stay idle while
 * the section is off screen (the original used the same trick to avoid
 * animating in a scrolled-past section).
 */
(function () {
  'use strict';

  function init(root) {
    if (!root || root.dataset.wfBound === '1') return;
    root.dataset.wfBound = '1';

    var stage = root.querySelector('[data-wf-stage]');
    var track = root.querySelector('[data-wf-track]');
    if (!track) return;

    var steps = track.querySelectorAll('[data-wf-step]');
    if (steps.length < 2) return;

    var interval = parseInt(root.getAttribute('data-interval'), 10) || 6000;
    var index = 0;
    var timer = null;
    var visible = false;
    var paused = false;

    // The track is a horizontal scrollport holding four steps against a 250px
    // min-width floor — 1000px of content, border-box. On the pages that ship a
    // stage image it is only about 518px wide at 1440 and 242px on a phone, so
    // autoplay kept advancing onto steps that were off screen: every visible
    // step sat dim for a whole interval with nothing moving, and the wrap back
    // to the first step read as a jump out of nowhere. Nothing here ever
    // scrolled the track. Now it follows the active step.
    var canSmooth = 'scrollBehavior' in document.documentElement.style;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');

    // An overflow smaller than a step's trailing padding, clamp(28px,3vw,44px),
    // is clipping dead space rather than text. Ignoring it is what keeps the
    // pages with no stage image perfectly still: one column gives them a 977px
    // track at 1440, which overruns 1000px of steps by 23px and would otherwise
    // twitch on every tick.
    var MIN_OVERFLOW = 40;

    function scrollToStep() {
      var el = steps[index];
      if (!el) return;

      var max = track.scrollWidth - track.clientWidth;
      if (max <= MIN_OVERFLOW) return;

      // Not el.scrollIntoView(): that walks up the ancestor chain and scrolls
      // every scrollable box it finds, the document included, so it would yank
      // the viewport away from whoever is reading. Only the track may move.
      //
      // Not offsetLeft either. The steps are position:relative but the track is
      // static, and so is everything above it, so their offsetParent resolves
      // all the way to <body> and offsetLeft is the step's absolute x in the
      // document. Feeding that to scrollTo pins the track at max scroll on the
      // first step and leaves it there. Rect deltas are measured against the
      // track itself and hold whatever the ancestors do.
      var left =
        track.scrollLeft +
        (el.getBoundingClientRect().left - track.getBoundingClientRect().left) -
        track.clientLeft;

      if (left < 0) left = 0;
      if (left > max) left = max;
      // Sub-pixel drift out of the clamp() paddings is not worth a scroll.
      if (Math.abs(left - track.scrollLeft) < 1) return;

      if (canSmooth && !(reduceMotion && reduceMotion.matches)) {
        track.scrollTo({ left: left, behavior: 'smooth' });
      } else {
        // Assigning scrollLeft obeys the CSS scroll-behavior, which the section
        // forces back to auto under reduced motion. Engines with no smooth
        // support at all land in the right place instantly.
        track.scrollLeft = left;
      }
    }

    function paint() {
      track.setAttribute('data-step', String(index));
      if (stage) stage.setAttribute('data-step', String(index));
    }

    // Re-triggering the CSS bar animation needs the attribute to actually
    // change, so clear it for a frame before repainting.
    function restartBar() {
      track.removeAttribute('data-step');
      // force a reflow so the removal is observed
      void track.offsetWidth;
      paint();
    }

    function stop() {
      clearInterval(timer);
      timer = null;
    }

    function start() {
      stop();
      if (!visible || paused) return;
      // Start the bar with the interval that will advance it. Resuming from a
      // hover used to restart this timer from zero while the bar carried on
      // from wherever it froze, so the two drifted apart.
      restartBar();
      // scrollToStep() hangs off the two places index actually changes rather
      // than off restartBar(), which start() also calls -- and start() is the
      // mouseleave resume path, so hooking there would snap the track back the
      // moment someone dragged it sideways and moved the pointer away.
      timer = setInterval(function () {
        index = (index + 1) % steps.length;
        scrollToStep();
        restartBar();
      }, interval);
    }

    for (var i = 0; i < steps.length; i++) {
      (function (el, n) {
        el.addEventListener('click', function () {
          index = n;
          scrollToStep();
          restartBar();
          start();
        });
      })(steps[i], i);
    }

    function hold() {
      paused = true;
      track.setAttribute('data-paused', 'true');
      stop();
    }
    function release() {
      paused = false;
      track.setAttribute('data-paused', 'false');
      start();
    }

    track.addEventListener('mouseenter', hold);
    track.addEventListener('mouseleave', release);

    // Now that the track moves, two ways of reading it that could never be
    // interrupted before can be. Neither fires a mouseenter.
    //
    // A phone: swipe across to reach the last step and the next tick scrolls it
    // straight back off screen. Same shape as pillar-capabilities.js.
    track.addEventListener('touchstart', hold, { passive: true });
    track.addEventListener('touchend', release, { passive: true });
    // A keyboard: the steps are real buttons, so tabbing to one scrolls the
    // track to it, and a tick later autoplay would scroll it away with the
    // focus ring still on it.
    track.addEventListener('focusin', hold);
    track.addEventListener('focusout', release);

    // The progress bar is a CSS animation keyed off data-step on the track, so it
    // runs the moment that attribute exists. The markup ships data-step="0" so
    // the first step still reads with JavaScript off -- but once this script is
    // running the attribute belongs to it. Drop it here and let start() set it
    // when the section is actually in view.
    //
    // Without this the bar filled and completed during page load, while the
    // interval that advances it waited on the IntersectionObserver. Anyone
    // scrolling down more than one interval after load met a finished progress
    // line sitting still, and it stayed still until the first tick fired.
    track.removeAttribute('data-step');
    if (stage) stage.setAttribute('data-step', String(index));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          visible = entries[0].isIntersecting;
          if (visible) start();
          else stop();
        },
        { rootMargin: '120px' }
      ).observe(root);
    } else {
      visible = true;
      start();
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-wf-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
